import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import ToolbarFilterDropdown from '../components/ToolbarFilterDropdown';
import './Customers.css';
import './BillingHome.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function BillingHome() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(searchParams.get('tab') === 'invoices' ? 'invoices' : 'billing');
  const setTab = t => {
    setTabState(t);
    setSearchParams(t === 'invoices' ? { tab: 'invoices' } : {}, { replace: true });
  };

  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [invSearch, setInvSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const pageSizeDdRef = useRef(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(null);

  useEffect(() => {
    let mounted = true;
    setCustomersLoading(true);
    api
      .get('/billing/customers')
      .then(rows => mounted && setCustomers(rows || []))
      .catch(() => mounted && setCustomers([]))
      .finally(() => mounted && setCustomersLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (tab !== 'invoices') return undefined;
    let mounted = true;
    setInvoicesLoading(true);
    api
      .get('/invoices')
      .then(rows => mounted && setInvoices(rows || []))
      .catch(() => mounted && setInvoices([]))
      .finally(() => mounted && setInvoicesLoading(false));
    return () => {
      mounted = false;
    };
  }, [tab]);

  useEffect(() => {
    if (!pageSizeOpen) return undefined;
    const close = () => setPageSizeOpen(false);
    const onDoc = e => {
      if (pageSizeDdRef.current && !pageSizeDdRef.current.contains(e.target)) close();
    };
    const onKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pageSizeOpen]);

  const filtered = useMemo(
    () =>
      customers.filter(c => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          (c.legal_name || '').toLowerCase().includes(q) ||
          (c.trade_name || '').toLowerCase().includes(q) ||
          (c.customer_id || '').toLowerCase().includes(q)
        );
      }),
    [customers, search]
  );

  const filteredInv = useMemo(() => {
    const q = invSearch.toLowerCase();
    return invoices.filter(i => {
      const matchQ =
        !q ||
        (i.invoice_id || '').toLowerCase().includes(q) ||
        (i.client_name || '').toLowerCase().includes(q) ||
        (i.sow_id || '').toLowerCase().includes(q) ||
        (i.resource_name || '').toLowerCase().includes(q);
      const matchS = !statusFilter || i.status === statusFilter;
      return matchQ && matchS;
    });
  }, [invoices, invSearch, statusFilter]);

  const statuses = useMemo(() => {
    const uniq = Array.from(new Set(invoices.map(i => i.status).filter(Boolean)));
    return uniq.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [invoices]);

  const statusFilterOptions = useMemo(
    () => [{ value: '', label: 'All statuses' }, ...statuses.map(s => ({ value: s, label: s }))],
    [statuses]
  );

  const activeList = tab === 'billing' ? filtered : filteredInv;

  useEffect(() => {
    setPage(1);
  }, [tab, search, invSearch, statusFilter, pageSize]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(activeList.length / pageSize));
    setPage(p => Math.min(p, tp));
  }, [activeList.length, pageSize]);

  const totalFiltered = activeList.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const offset = (safePage - 1) * pageSize;
  const pageRows = activeList.slice(offset, offset + pageSize);
  const rangeStart = totalFiltered === 0 ? 0 : offset + 1;
  const rangeEnd = totalFiltered === 0 ? 0 : Math.min(offset + pageSize, totalFiltered);

  const billingHasFilters = !!search;
  const invHasFilters = !!(invSearch || statusFilter);
  const clearBillingFilters = () => setSearch('');
  const clearInvFilters = () => {
    setInvSearch('');
    setStatusFilter('');
  };

  return (
    <>
      <div className="page-header">
        <h1>Billing</h1>
      </div>
      <div className="page-content billing-home-page clause-library-page">
        <div className="clause-lib-tab-strip" role="tablist" aria-label="Billing sections">
          <button
            type="button"
            role="tab"
            id="billing-tab-home"
            aria-selected={tab === 'billing'}
            className={`clause-lib-tab ${tab === 'billing' ? 'clause-lib-tab-active' : ''}`}
            onClick={() => setTab('billing')}
          >
            Billing
          </button>
          <button
            type="button"
            role="tab"
            id="billing-tab-invoices"
            aria-selected={tab === 'invoices'}
            aria-controls="billing-invoices-panel"
            className={`clause-lib-tab ${tab === 'invoices' ? 'clause-lib-tab-active' : ''}`}
            onClick={() => setTab('invoices')}
          >
            Invoices
            {!invoicesLoading && invoices.length > 0 && (
              <span className="billing-home-tab-count">({invoices.length})</span>
            )}
          </button>
        </div>

        {tab === 'billing' && (
          <>
            <div className="customers-top">
              <div className="customers-stats" aria-label="Customer counts">
                <div className="customers-stat">
                  <div className="customers-stat-label">Customers</div>
                  <div className="customers-stat-value">{customersLoading ? '—' : customers.length.toLocaleString()}</div>
                </div>
                <div className="customers-stat">
                  <div className="customers-stat-label">Match search</div>
                  <div className="customers-stat-value">{customersLoading ? '—' : filtered.length.toLocaleString()}</div>
                </div>
              </div>
              <div className="customers-top-right">
                {billingHasFilters && (
                  <button type="button" className="btn btn-outline btn-sm" onClick={clearBillingFilters}>
                    Clear search
                  </button>
                )}
              </div>
            </div>

            {customers.length > 0 && filtered.length < customers.length && (
              <div className="customers-filter-hint" role="status">
                <span>
                  Search narrows the list to <strong>{filtered.length}</strong> of <strong>{customers.length}</strong>{' '}
                  customer{customers.length === 1 ? '' : 's'}.
                </span>
                <button type="button" className="btn btn-outline btn-sm" onClick={clearBillingFilters}>
                  Clear search
                </button>
              </div>
            )}

            <div className="customers-toolbar">
              <div className="customers-toolbar-left">
                <div className="customers-search">
                  <span className="customers-search-icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path d="M16.3 16.3 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by customer name, trade name, or ID…"
                    aria-label="Search billing customers"
                  />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">Customers</div>
              <div className="billing-home-customers-table">
                <table>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Default currency</th>
                      <th>Std unit type</th>
                      <th>Active SOWs</th>
                      <th>MSAs</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(c => (
                      <tr key={c.customer_id}>
                        <td>
                          <div className="billing-home-customer-name">{c.legal_name}</div>
                          <div className="billing-home-customer-meta">
                            {c.customer_id}
                            {c.trade_name ? ` · ${c.trade_name}` : ''}
                          </div>
                        </td>
                        <td>{c.default_currency}</td>
                        <td>{c.std_unit_type || 'None'}</td>
                        <td className="tabular-nums text-right">{c.active_sow_count}</td>
                        <td className="tabular-nums text-right">{c.msa_count}</td>
                        <td className="text-right">
                          <Link to={`/invoices/customer/${c.customer_id}`} className="btn btn-primary btn-sm">
                            Open Billing
                          </Link>
                        </td>
                      </tr>
                    ))}
                    {!filtered.length && (
                      <tr>
                        <td colSpan="6" className="billing-home-empty">
                          {customersLoading ? 'Loading customers…' : 'No customers match your search.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {!customersLoading && totalFiltered > 0 && (
                <PaginationFooter
                  totalFiltered={totalFiltered}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  safePage={safePage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  pageSizeOpen={pageSizeOpen}
                  setPageSizeOpen={setPageSizeOpen}
                  setPageSize={setPageSize}
                  setPage={setPage}
                  pageSizeDdRef={pageSizeDdRef}
                  ariaLabelId="billing-home-page-size-label"
                />
              )}
            </div>
          </>
        )}

        {tab === 'invoices' && (
          <div id="billing-invoices-panel" role="tabpanel" aria-labelledby="billing-tab-invoices">
            <div className="customers-top">
              <div className="customers-stats" aria-label="Invoice counts">
                <div className="customers-stat">
                  <div className="customers-stat-label">Invoices</div>
                  <div className="customers-stat-value">{invoicesLoading ? '—' : invoices.length.toLocaleString()}</div>
                </div>
                <div className="customers-stat">
                  <div className="customers-stat-label">Match filters</div>
                  <div className="customers-stat-value">{invoicesLoading ? '—' : filteredInv.length.toLocaleString()}</div>
                </div>
              </div>
              <div className="customers-top-right">
                {invHasFilters && (
                  <button type="button" className="btn btn-outline btn-sm" onClick={clearInvFilters}>
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            {invoices.length > 0 && filteredInv.length < invoices.length && (
              <div className="customers-filter-hint" role="status">
                <span>
                  Filters narrow the table to <strong>{filteredInv.length}</strong> of <strong>{invoices.length}</strong>{' '}
                  invoice{invoices.length === 1 ? '' : 's'}.
                </span>
                <button type="button" className="btn btn-outline btn-sm" onClick={clearInvFilters}>
                  Clear filters
                </button>
              </div>
            )}

            <div className="customers-toolbar billing-home-invoice-toolbar">
              <div className="customers-toolbar-left">
                <div className="customers-search">
                  <span className="customers-search-icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path d="M16.3 16.3 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  <input
                    value={invSearch}
                    onChange={e => setInvSearch(e.target.value)}
                    placeholder="Search invoice, client, SOW, resource…"
                    aria-label="Search invoices"
                  />
                </div>
                <div className="customers-filters">
                  <ToolbarFilterDropdown
                    id="billing-inv-filter-status"
                    labelText="Status"
                    ariaLabel="Filter by invoice status"
                    value={statusFilter}
                    onChange={setStatusFilter}
                    placeholder="All statuses"
                    options={statusFilterOptions}
                    menuKey="billing-inv-status"
                    openMenu={filterMenuOpen}
                    setOpenMenu={setFilterMenuOpen}
                  />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">Generated invoices</div>
              <div className="billing-home-invoices-table">
                <table>
                  <thead>
                    <tr>
                      <th>Invoice ID</th>
                      <th>Date</th>
                      <th>Client</th>
                      <th>SOW</th>
                      <th>Resource</th>
                      <th>Period</th>
                      <th>Currency</th>
                      <th className="text-right">Total</th>
                      <th>Due</th>
                      <th>Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(i => (
                      <tr key={i.invoice_id}>
                        <td>
                          <Link to={`/invoices/view/${i.invoice_id}`} className="link">
                            {i.invoice_id}
                          </Link>
                        </td>
                        <td>{i.invoice_date}</td>
                        <td title={i.client_name}>{i.client_name}</td>
                        <td>
                          {i.sow_id ? (
                            <Link to={`/sow/${i.sow_id}`} className="link">
                              {i.sow_id}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td title={i.resource_name || ''}>{i.resource_name || '—'}</td>
                        <td className="billing-home-period-cell">
                          {i.period_from} → {i.period_to}
                        </td>
                        <td>{i.invoice_currency || i.agreed_currency}</td>
                        <td className="billing-home-total-cell">{Number(i.total || 0).toLocaleString()}</td>
                        <td>{i.due_date}</td>
                        <td>
                          <span className={`badge badge-${(i.status || '').toLowerCase()}`}>{i.status}</span>
                        </td>
                        <td className="text-right">
                          <Link to={`/invoices/view/${i.invoice_id}`} className="btn btn-sm btn-outline">
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                    {!filteredInv.length && (
                      <tr>
                        <td colSpan="11" className="billing-home-empty">
                          {invoicesLoading
                            ? 'Loading invoices…'
                            : 'No invoices match the filters. Use the Billing tab to open a customer and generate invoices.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {!invoicesLoading && totalFiltered > 0 && (
                <PaginationFooter
                  totalFiltered={totalFiltered}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  safePage={safePage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  pageSizeOpen={pageSizeOpen}
                  setPageSizeOpen={setPageSizeOpen}
                  setPageSize={setPageSize}
                  setPage={setPage}
                  pageSizeDdRef={pageSizeDdRef}
                  ariaLabelId="billing-home-page-size-label"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function PaginationFooter({
  totalFiltered,
  rangeStart,
  rangeEnd,
  safePage,
  totalPages,
  pageSize,
  pageSizeOpen,
  setPageSizeOpen,
  setPageSize,
  setPage,
  pageSizeDdRef,
  ariaLabelId,
}) {
  return (
    <div className="customers-pagination" role="navigation" aria-label="Table pagination">
      <div className="customers-pagination-summary">
        <span className="customers-pagination-summary-label">Showing</span>
        <span className="customers-pagination-summary-range">
          <strong className="tabular-nums">{rangeStart}</strong>
          <span className="customers-pagination-summary-dash">–</span>
          <strong className="tabular-nums">{rangeEnd}</strong>
          <span className="customers-pagination-summary-of">of</span>
          <strong className="tabular-nums">{totalFiltered.toLocaleString()}</strong>
          <span className="customers-pagination-summary-unit">rows</span>
        </span>
      </div>
      <div className="customers-pagination-controls">
        <div className="customers-pagination-page-size" ref={pageSizeDdRef}>
          <span className="customers-pagination-label" id={ariaLabelId}>
            Rows per page
          </span>
          <div className="customers-page-size-dd-inner">
            <button
              type="button"
              className={`customers-page-size-trigger ${pageSizeOpen ? 'customers-page-size-trigger-open' : ''}`}
              aria-haspopup="listbox"
              aria-expanded={pageSizeOpen}
              aria-label={`Rows per page, current value ${pageSize}`}
              onClick={() => setPageSizeOpen(o => !o)}
            >
              <span>{pageSize}</span>
              <span className="customers-page-size-chevron" aria-hidden>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </span>
            </button>
            {pageSizeOpen && (
              <ul className="customers-page-size-menu" role="listbox" aria-labelledby={ariaLabelId}>
                {PAGE_SIZE_OPTIONS.map(n => (
                  <li
                    key={n}
                    role="option"
                    aria-selected={pageSize === n}
                    className={
                      pageSize === n ? 'customers-page-size-option customers-page-size-option-active' : 'customers-page-size-option'
                    }
                    onMouseDown={e => {
                      e.preventDefault();
                      setPageSize(n);
                      setPageSizeOpen(false);
                    }}
                  >
                    {n}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="customers-pagination-pager" role="group" aria-label="Page navigation">
          <button
            type="button"
            className="customers-pagination-step"
            disabled={safePage <= 1}
            onClick={() => setPage(1)}
            aria-label="First page"
            title="First page"
          >
            <span className="customers-pagination-step-icon" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 19l-7-7 7-7" />
                <path d="M18 19l-7-7 7-7" />
              </svg>
            </span>
            <span>First</span>
          </button>
          <button
            type="button"
            className="customers-pagination-step"
            disabled={safePage <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            aria-label="Previous page"
            title="Previous page"
          >
            <span className="customers-pagination-step-icon" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </span>
            <span>Prev</span>
          </button>
          <div className="customers-pagination-page-chip">
            <span className="customers-pagination-page-label">Page</span>
            <span className="customers-pagination-page-current tabular-nums">{safePage}</span>
            <span className="customers-pagination-page-sep">/</span>
            <span className="customers-pagination-page-total tabular-nums">{totalPages}</span>
          </div>
          <button
            type="button"
            className="customers-pagination-step"
            disabled={safePage >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            aria-label="Next page"
            title="Next page"
          >
            <span>Next</span>
            <span className="customers-pagination-step-icon" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </span>
          </button>
          <button
            type="button"
            className="customers-pagination-step"
            disabled={safePage >= totalPages}
            onClick={() => setPage(totalPages)}
            aria-label="Last page"
            title="Last page"
          >
            <span>Last</span>
            <span className="customers-pagination-step-icon" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 5l7 7-7 7" />
                <path d="M6 5l7 7-7 7" />
              </svg>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
